import { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { bulkUploadStore } from '../../lib/stores/bulkUploadStore'
import {
  Box, Button, Typography, CircularProgress, Divider, Drawer, IconButton,
  Select, MenuItem, FormControl, InputLabel, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, LinearProgress, Alert,
  Collapse,
} from '@mui/material'
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
  ArrowForward as NextIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material'
import Papa from 'papaparse'
import { useQuery } from '@tanstack/react-query'
import { authStore } from '../../lib/stores/authStore'
import { themeColors } from '../../app/layout/theme'
import { leadsApi, INDUSTRY_TYPES, BulkUploadJobDto, PaginatedBulkUploadJobLogs } from '../../lib/api/leadsApi'
import { orgsApi, lobsApi } from '../../lib/api/tenantsApi'
import { ConfirmDialog } from '../shared/ConfirmDialog'
import { toast } from 'react-toastify'

// ─── Constants ─────────────────────────────────────────────────────────────────

const MAX_FILE_BYTES = 100 * 1024 * 1024 // 100 MB

/** Fixed 1-to-1 mapping — template column names match CRM field names */
const FIXED_MAPPINGS: Record<string, string> = {
  Name: 'Name', Phone: 'Phone', Email: 'Email', Source: 'Source',
  CompanyName: 'CompanyName', Address: 'Address', Gst: 'Gst', Pincode: 'Pincode',
  Designation: 'Designation', WebsiteUrl: 'WebsiteUrl', IndustryType: 'IndustryType',
}

// Displayed column headers for the template (mandatory ones marked with *)
const TEMPLATE_COLS_DISPLAY = ['Name *', 'Phone *', 'Email', 'Source', 'CompanyName', 'Address', 'Gst', 'Pincode', 'Designation', 'Website URL', 'Industry']
// Actual CSV column keys (no asterisk — must match server mapping)
const TEMPLATE_COLS = ['Name', 'Phone', 'Email', 'Source', 'CompanyName', 'Address', 'Gst', 'Pincode', 'Designation', 'WebsiteUrl', 'IndustryType']

// Indian GSTIN regex
const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/

// Valid phone: 10 digits, starts with 6–9
const PHONE_REGEX = /^[6-9][0-9]{9}$/

function fmtBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// ─── Validation types ──────────────────────────────────────────────────────────

type IssueSeverity = 'error' | 'warning'

interface ValidationIssue {
  row: number
  field: string
  message: string
  severity: IssueSeverity
}

function validateAllRows(rows: CsvRow[]): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const seenPhones = new Set<string>()
  const seenEmails = new Set<string>()

  rows.forEach((row, idx) => {
    const rowNum = idx + 2 // +1 for 0-index, +1 because row 1 is header

    const name = (row['Name'] || '').trim()
    const phoneRaw = (row['Phone'] || '').trim()
    const email = (row['Email'] || '').trim()
    const gst = (row['Gst'] || '').trim()

    // Mandatory: Name
    if (!name) {
      issues.push({ row: rowNum, field: 'Name', message: 'Name is required.', severity: 'error' })
    }

    // Mandatory: Phone
    if (!phoneRaw) {
      issues.push({ row: rowNum, field: 'Phone', message: 'Phone number is required.', severity: 'error' })
    } else {
      const phone = phoneRaw.replace(/\D/g, '')
      if (!PHONE_REGEX.test(phone)) {
        issues.push({
          row: rowNum, field: 'Phone',
          message: `'${phoneRaw}' is not a valid Indian mobile number (10 digits, starting with 6–9).`,
          severity: 'error',
        })
      } else {
        // Phone number validated (duplicate phone check removed)
        seenPhones.add(phone)
      }
    }

    // Email format
    if (email && !email.includes('@')) {
      issues.push({
        row: rowNum, field: 'Email',
        message: `'${email}' is not a valid email address.`,
        severity: 'error',
      })
    }

    // GST validation
    if (gst) {
      const gstNorm = gst.toUpperCase()
      if (!GST_REGEX.test(gstNorm)) {
        issues.push({
          row: rowNum, field: 'GST',
          message: `'${gst}' is not a valid GSTIN. Format: 27AADCB2230M1Z2 (15 characters).`,
          severity: 'error',
        })
      }
    }
  })

  return issues
}

// ─── Step indicator ────────────────────────────────────────────────────────────

function StepBar({ step }: { step: number }) {
  const steps = ['Upload', 'Preview', 'Processing', 'Done']
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', px: 3, pt: 2, pb: 1.5 }}>
      {steps.map((label, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', flex: i < steps.length - 1 ? 1 : 'none' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4 }}>
            <Box sx={{
              width: 26, height: 26, borderRadius: '50%', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              fontSize: '11px', fontWeight: 700,
              bgcolor: i < step ? '#12B76A' : i === step ? themeColors.primary : '#E4E7EC',
              color: i <= step ? '#fff' : '#98A2B3',
              transition: 'all 0.3s',
            }}>
              {i < step ? '✓' : i + 1}
            </Box>
            <Typography sx={{
              fontSize: '10px', fontWeight: i === step ? 700 : 400,
              color: i === step ? themeColors.primary : i < step ? '#12B76A' : '#98A2B3',
              whiteSpace: 'nowrap',
            }}>
              {label}
            </Typography>
          </Box>
          {i < steps.length - 1 && (
            <Box sx={{
              flex: 1, height: 2, mx: 1, mb: 2.5,
              bgcolor: i < step ? '#12B76A' : '#E4E7EC',
              transition: 'background 0.3s',
            }} />
          )}
        </Box>
      ))}
    </Box>
  )
}

// ─── Validation Issues Panel ───────────────────────────────────────────────────

function ValidationIssuesPanel({ issues }: { issues: ValidationIssue[] }) {
  const [expanded, setExpanded] = useState(true)

  const errorCount   = issues.filter(i => i.severity === 'error').length
  const warningCount = issues.filter(i => i.severity === 'warning').length

  if (issues.length === 0) return null

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 2.5, borderRadius: '10px',
        borderColor: errorCount > 0 ? '#FECDCA' : '#FEC84B',
        bgcolor: errorCount > 0 ? '#FEF3F2' : '#FFFAEB',
        overflow: 'hidden',
      }}
    >
      {/* Header row */}
      <Box
        onClick={() => setExpanded(v => !v)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1.25, cursor: 'pointer',
          '&:hover': { bgcolor: errorCount > 0 ? '#FEE4E2' : '#FEF0C7' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {errorCount > 0
            ? <ErrorIcon sx={{ fontSize: 16, color: '#B42318' }} />
            : <WarningIcon sx={{ fontSize: 16, color: '#B54708' }} />
          }
          <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: errorCount > 0 ? '#B42318' : '#B54708' }}>
            Validation Issues Found
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {errorCount > 0 && (
              <Chip
                size="small"
                label={`${errorCount} error${errorCount > 1 ? 's' : ''}`}
                sx={{ height: 18, fontSize: '10px', fontWeight: 700, bgcolor: '#FEE4E2', color: '#B42318', border: '1px solid #FECDCA' }}
              />
            )}
            {warningCount > 0 && (
              <Chip
                size="small"
                label={`${warningCount} warning${warningCount > 1 ? 's' : ''}`}
                sx={{ height: 18, fontSize: '10px', fontWeight: 700, bgcolor: '#FEF0C7', color: '#B54708', border: '1px solid #FEC84B' }}
              />
            )}
          </Box>
        </Box>
        <IconButton size="small" sx={{ p: 0.25, color: errorCount > 0 ? '#B42318' : '#B54708' }}>
          {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Divider sx={{ borderColor: errorCount > 0 ? '#FECDCA' : '#FEC84B' }} />

        {/* Sub-header description */}
        <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
          <Typography sx={{ fontSize: '11.5px', color: errorCount > 0 ? '#912018' : '#93370D' }}>
            {errorCount > 0
              ? `Rows with errors will be skipped during import. Fix them in your file and re-upload, or proceed to import only the valid rows.`
              : `Rows with warnings (within-file duplicates) will be skipped. Valid rows will still be imported.`
            }
          </Typography>
        </Box>

        {/* Issues table */}
        <TableContainer sx={{ maxHeight: 220 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {['Row', 'Field', 'Issue'].map(h => (
                  <TableCell key={h} sx={{
                    fontSize: '10px', fontWeight: 700, py: 0.75, px: 1.5,
                    bgcolor: errorCount > 0 ? '#FEE4E2' : '#FEF0C7',
                    color: errorCount > 0 ? '#912018' : '#93370D',
                    whiteSpace: 'nowrap',
                    borderBottom: `1px solid ${errorCount > 0 ? '#FECDCA' : '#FEC84B'}`,
                  }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {issues.map((issue, idx) => (
                <TableRow key={idx} sx={{ '&:last-child td': { border: 0 } }}>
                  <TableCell sx={{ fontSize: '11px', py: 0.75, px: 1.5, fontWeight: 700, color: issue.severity === 'error' ? '#B42318' : '#B54708', whiteSpace: 'nowrap' }}>
                    Row {issue.row}
                  </TableCell>
                  <TableCell sx={{ fontSize: '11px', py: 0.75, px: 1.5, color: '#344054', whiteSpace: 'nowrap' }}>
                    <Chip
                      label={issue.field}
                      size="small"
                      sx={{ height: 16, fontSize: '10px', fontWeight: 600, bgcolor: '#F2F4F7', color: '#344054' }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: '11px', py: 0.75, px: 1.5, color: '#344054' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                      {issue.severity === 'error'
                        ? <ErrorIcon sx={{ fontSize: 12, color: '#B42318', mt: '1px', flexShrink: 0 }} />
                        : <WarningIcon sx={{ fontSize: 12, color: '#B54708', mt: '1px', flexShrink: 0 }} />
                      }
                      {issue.message}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Paper>
  )
}

// ─── Inline Upload Error Panel ─────────────────────────────────────────────────

interface UploadError {
  title: string
  detail?: string
  logs?: string[]
}

function UploadErrorPanel({ error, onDismiss }: { error: UploadError; onDismiss: () => void }) {
  const [expanded, setExpanded] = useState(true)

  return (
    <Paper
      variant="outlined"
      sx={{ mt: 2.5, borderRadius: '10px', borderColor: '#FECDCA', bgcolor: '#FEF3F2', overflow: 'hidden' }}
    >
      <Box
        onClick={() => setExpanded(v => !v)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1.25, cursor: 'pointer',
          '&:hover': { bgcolor: '#FEE4E2' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorIcon sx={{ fontSize: 16, color: '#B42318' }} />
          <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: '#B42318' }}>
            Import Failed
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton size="small" sx={{ p: 0.25, color: '#B42318' }} onClick={(e) => { e.stopPropagation(); onDismiss() }}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
          <IconButton size="small" sx={{ p: 0.25, color: '#B42318' }}>
            {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Divider sx={{ borderColor: '#FECDCA' }} />
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontSize: '12px', color: '#912018', fontWeight: 600, mb: 0.5 }}>
            {error.title}
          </Typography>
          {error.detail && (
            <Typography sx={{ fontSize: '11.5px', color: '#912018', mb: error.logs?.length ? 1.5 : 0 }}>
              {error.detail}
            </Typography>
          )}
          {error.logs && error.logs.length > 0 && (
            <>
              <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#B42318', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75 }}>
                Server Details
              </Typography>
              <Box sx={{
                fontFamily: 'monospace', fontSize: '11px',
                bgcolor: '#1A1A2E', color: '#E2E8F0',
                p: 1.25, borderRadius: '6px', maxHeight: 160, overflowY: 'auto',
              }}>
                {error.logs.map((log, i) => (
                  <Box key={i} sx={{ py: '2px', color: '#F04438' }}>{log}</Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  )
}

// ─── Component ─────────────────────────────────────────────────────────────────

interface Props {
  open?: boolean
  onClose?: () => void
  onSuccess?: () => void
  onOpen?: () => void
}

interface CsvRow { [col: string]: string }

export const BulkImportLeadsDrawer = observer(function BulkImportLeadsDrawer({ open: propOpen, onClose, onSuccess, onOpen }: Props) {
  const userOrgId = authStore.user?.orgId || ''
  const userLobId = authStore.user?.lobId || ''

  // Data-driven tenant detection (role-agnostic):
  //  needsOrgAndLob — no org assigned at all (also implies no lob)
  //  needsLobOnly   — has an org but no lob
  //  neither        — both assigned, proceed silently
  const needsOrgAndLob = !userOrgId
  const needsLobOnly   = !!userOrgId && !userLobId

  // Destructure state from store for local read access in JSX
  const {
    step,
    selectedFile,
    fileError,
    isDragging,
    previewRows,
    allRows,
    rowCount,
    validationIssues,
    selOrgId,
    selLobId,
    uploadPct,
    isUploading,
    uploadError,
    jobId,
    jobStatus,
    elapsedSeconds,
    logsPage,
    logsResult,
    confirmOpen,
    showGuidelines,
    hasBlockingErrors,
    validRowCount,
  } = bulkUploadStore

  const open = propOpen !== undefined ? propOpen : bulkUploadStore.open

  // ── tenant queries ───────────────────────────────────────────────────────
  // needsOrgAndLob: fetch all orgs so user can pick one, then fetch its LOBs
  // needsLobOnly  : org is fixed (userOrgId), only fetch LOBs for it
  const { data: orgs = [] } = useQuery({
    queryKey: ['admin-orgs-help'], queryFn: orgsApi.getAll,
    enabled: open && needsOrgAndLob, staleTime: 60_000,
  })
  const lobQueryOrgId = needsOrgAndLob ? selOrgId : userOrgId
  const { data: lobs = [] } = useQuery({
    queryKey: ['admin-lobs-help', lobQueryOrgId],
    queryFn: () => lobsApi.getAll(lobQueryOrgId),
    enabled: open && (needsOrgAndLob || needsLobOnly) && !!lobQueryOrgId, staleTime: 60_000,
  })

  // Local wrappers mapping state functions to store actions
  const setStep = (val: number) => bulkUploadStore.setStep(val)
  const setSelOrgId = (val: string) => bulkUploadStore.setSelOrgId(val)
  const setSelLobId = (val: string) => bulkUploadStore.setSelLobId(val)
  const setShowGuidelines = (val: boolean) => bulkUploadStore.setShowGuidelines(val)
  const setConfirmOpen = (val: boolean) => bulkUploadStore.setConfirmOpen(val)
  const fetchLogs = (id: string, page: number) => bulkUploadStore.fetchLogs(id, page)
  const handleCancel = () => bulkUploadStore.handleCancel()
  const setIsDragging = (val: boolean) => bulkUploadStore.setIsDragging(val)
  const setUploadError = (val: any) => bulkUploadStore.setUploadError(val)

  const handleAttemptClose = () => {
    if (selectedFile && step < 2 && !isUploading) {
      bulkUploadStore.setConfirmOpen(true)
      return
    }
    if (!isUploading && step !== 2) {
      bulkUploadStore.setOpen(false)
      if (onClose) onClose()
      if (step === 3) {
        setTimeout(() => {
          bulkUploadStore.reset()
        }, 300)
      }
    }
  }

  const handleDismissProgress = (e: React.MouseEvent) => {
    e.stopPropagation()
    bulkUploadStore.reset()
  }

  const downloadTemplate = () => {
    // Header row with mandatory markers shown in the first comment row
    const noteRow = ['* = mandatory field', '', '', '', '', '', '', '', '', '', '']
    const sampleRow = ['John Doe', '9876543210', 'john@example.com', 'Facebook', 'Acme Corp', '123 Main St', '27AADCB2230M1Z2', '400001', 'Manager', 'https://example.com', 'Technology']
    const csv = Papa.unparse([TEMPLATE_COLS_DISPLAY, noteRow, sampleRow])
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }))
    const a = document.createElement('a')
    a.href = url; a.download = 'leads_import_template.csv'; a.click()
  }

  const processFile = (file: File) => {
    bulkUploadStore.handleFileInput(file)
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) bulkUploadStore.handleFileInput(e.target.files[0])
    e.target.value = ''
  }

  const handleImport = async () => {
    // Resolve the final orgId / lobId to send to the backend:
    //  needsOrgAndLob → user selected both from the drawer
    //  needsLobOnly   → org is fixed (userOrgId), lob was selected from the drawer
    //  normal user    → both come from their profile; backend resolves from JWT
    const resolvedOrgId = needsOrgAndLob ? selOrgId : needsLobOnly ? userOrgId : undefined
    const resolvedLobId = (needsOrgAndLob || needsLobOnly) ? selLobId : undefined
    await bulkUploadStore.startImport(FIXED_MAPPINGS, resolvedOrgId, resolvedLobId)
  }

  const formatDuration = (sec: number) => {
    const m = Math.floor(sec / 60)
    const s = sec % 60
    return `${m}m ${s}s`
  }

  const uploadController = {
    abort: () => bulkUploadStore.cancelUpload()
  }

  // ── canProceed guards ─────────────────────────────────────────────────────
  // needsOrgAndLob: block until both selOrgId and selLobId are chosen
  // needsLobOnly  : block until selLobId is chosen
  // normal user   : always ready
  const tenantReady = needsOrgAndLob
    ? (!!selOrgId && !!selLobId)
    : needsLobOnly
      ? !!selLobId
      : true
  const canGoPreview = !!selectedFile && !fileError && tenantReady

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleAttemptClose}
        PaperProps={{
          sx: { width: { xs: '100vw', sm: 600 }, maxWidth: '100vw', bgcolor: '#fff', boxShadow: '-4px 0 32px rgba(0,0,0,0.1)' },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

          {/* ── Header ── */}
          <Box sx={{ px: 2.5, pt: 2, pb: 0, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: `${themeColors.primary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <UploadIcon sx={{ color: themeColors.primary, fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: themeColors.text.primary, lineHeight: 1.2 }}>
                    Bulk Lead Upload
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Import leads from a CSV file
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={handleAttemptClose} size="small" disabled={isUploading || step === 2} sx={{ color: '#98A2B3' }}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            <StepBar step={step} />
          </Box>
          <Divider />

          {/* ── Content ── */}
          <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>

            {/* ══ STEP 0 : UPLOAD ══════════════════════════════════════════ */}
            {step === 0 && (
              <Box>

                {/* ── No org assigned: user must choose Org + LOB ── */}
                {needsOrgAndLob && (
                  <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: '10px', bgcolor: '#FAFBFF', borderColor: `${themeColors.primary}30` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 700, color: themeColors.text.primary }}>
                        Target Destination
                      </Typography>
                      <Typography variant="caption" color="error" sx={{ ml: 0.5 }}>*</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      You don't have an organisation assigned. Select an organisation and line of business for this upload.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <FormControl size="small" fullWidth>
                        <InputLabel sx={{ fontSize: '13px' }}>Organisation</InputLabel>
                        <Select
                          value={selOrgId}
                          label="Organisation"
                          onChange={e => setSelOrgId(e.target.value)}
                          sx={{ fontSize: '13px', borderRadius: '8px' }}
                        >
                          {orgs.map(o => <MenuItem key={o.id} value={o.id} sx={{ fontSize: '13px' }}>{o.name}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <FormControl size="small" fullWidth disabled={!selOrgId}>
                        <InputLabel sx={{ fontSize: '13px' }}>Line of Business</InputLabel>
                        <Select
                          value={selLobId}
                          label="Line of Business"
                          onChange={e => setSelLobId(e.target.value)}
                          sx={{ fontSize: '13px', borderRadius: '8px' }}
                        >
                          {lobs.map(l => <MenuItem key={l.id} value={l.id} sx={{ fontSize: '13px' }}>{l.name}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Box>
                  </Paper>
                )}

                {/* ── Has org but no LOB: user must choose LOB only ── */}
                {needsLobOnly && (
                  <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: '10px', bgcolor: '#FAFBFF', borderColor: `${themeColors.primary}30` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 700, color: themeColors.text.primary }}>
                        Select Line of Business
                      </Typography>
                      <Typography variant="caption" color="error" sx={{ ml: 0.5 }}>*</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      You don't have a default LOB assigned. Please select one for this upload.
                    </Typography>
                    <FormControl size="small" fullWidth>
                      <InputLabel sx={{ fontSize: '13px' }}>Line of Business</InputLabel>
                      <Select
                        value={selLobId}
                        label="Line of Business"
                        onChange={e => setSelLobId(e.target.value)}
                        sx={{ fontSize: '13px', borderRadius: '8px' }}
                      >
                        {lobs.map(l => <MenuItem key={l.id} value={l.id} sx={{ fontSize: '13px' }}>{l.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Paper>
                )}

                {/* Download Template */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <InfoIcon sx={{ fontSize: 14, color: themeColors.primary }} />
                    <Typography sx={{ fontSize: '11.5px', color: themeColors.text.secondary }}>
                      Mandatory fields are marked with <strong>*</strong> in the template
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<DownloadIcon sx={{ fontSize: 15 }} />}
                    onClick={downloadTemplate}
                    sx={{
                      fontSize: '12px', fontWeight: 600, textTransform: 'none',
                      color: themeColors.primary, borderColor: `${themeColors.primary}50`,
                      borderRadius: '6px', px: 1.5, py: 0.5, flexShrink: 0,
                      border: `1px solid ${themeColors.primary}50`,
                      '&:hover': { bgcolor: `${themeColors.primary}08` },
                    }}
                  >
                    Download CSV Template
                  </Button>
                </Box>

                {/* File Drop Zone */}
                <Box
                  component="label"
                  onDragOver={e => { e.preventDefault(); setIsDragging(true) }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={e => { e.preventDefault(); setIsDragging(false); const f = e.dataTransfer.files[0]; if (f) processFile(f) }}
                  sx={{
                    display: 'block', cursor: 'pointer',
                    border: `2px dashed ${isDragging ? themeColors.primary : fileError ? '#F04438' : selectedFile ? '#12B76A' : `${themeColors.primary}40`}`,
                    borderRadius: '12px', p: 4, textAlign: 'center',
                    bgcolor: isDragging ? `${themeColors.primary}06` : fileError ? '#FEF3F2' : selectedFile ? '#F6FEF9' : '#FAFBFF',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: themeColors.primary, bgcolor: `${themeColors.primary}06` },
                  }}
                >
                  <input type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFileInput} />

                  {selectedFile ? (
                    /* File loaded state */
                    <Box>
                      <Box sx={{
                        width: 52, height: 52, borderRadius: '12px',
                        bgcolor: '#ECFDF3', border: '1px solid #A6F4C5',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mx: 'auto', mb: 1.5,
                      }}>
                        <FileIcon sx={{ fontSize: 26, color: '#12B76A' }} />
                      </Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#027A48', mb: 0.25 }}>
                        {selectedFile.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtBytes(selectedFile.size)} · {rowCount} rows · click to replace
                      </Typography>
                    </Box>
                  ) : (
                    /* Empty / idle state */
                    <Box>
                      <Box sx={{
                        width: 52, height: 52, borderRadius: '12px',
                        bgcolor: `${themeColors.primary}10`, border: `1px solid ${themeColors.primary}25`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mx: 'auto', mb: 1.5,
                      }}>
                        <UploadIcon sx={{ fontSize: 26, color: themeColors.primary }} />
                      </Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '14px', color: themeColors.text.primary, mb: 0.25 }}>
                        {isDragging ? 'Drop it here!' : 'Click to upload or drag & drop'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Only .csv files · Max 100 MB
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* File error */}
                {fileError && (
                  <Alert severity="error" sx={{ mt: 1.5, borderRadius: '8px', fontSize: '12px' }}>
                    {fileError}
                  </Alert>
                )}

                {/* Quick validation summary on upload step */}
                {selectedFile && validationIssues.length > 0 && (
                  <Alert
                    severity={hasBlockingErrors ? 'error' : 'warning'}
                    sx={{ mt: 1.5, borderRadius: '8px', fontSize: '12px' }}
                  >
                    {hasBlockingErrors
                      ? `${validationIssues.filter(i => i.severity === 'error').length} row(s) have validation errors and will be skipped. Click "Preview" to see details.`
                      : `${validationIssues.filter(i => i.severity === 'warning').length} row(s) have warnings (within-file duplicates). Click "Preview" to review.`
                    }
                  </Alert>
                )}

                {selectedFile && validationIssues.length === 0 && rowCount > 0 && (
                  <Alert severity="success" sx={{ mt: 1.5, borderRadius: '8px', fontSize: '12px' }}>
                    All {rowCount} rows passed validation. Ready to import!
                  </Alert>
                )}

                {/* Guidelines & Accepted Values */}
                <Box sx={{ mt: 3 }}>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={showGuidelines ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => setShowGuidelines(!showGuidelines)}
                    sx={{ textTransform: 'none', fontWeight: 600, color: themeColors.primary, fontSize: '12.5px', p: 0 }}
                  >
                    {showGuidelines ? 'Hide Template Guidelines' : 'View Template Guidelines & Industry Options'}
                  </Button>
                  <Collapse in={showGuidelines}>
                    <Paper variant="outlined" sx={{ p: 2, mt: 1, borderRadius: '8px', bgcolor: '#FAFBFF', borderColor: '#E4E7EC' }}>
                      <Typography sx={{ fontSize: '12.0px', fontWeight: 700, mb: 1, color: themeColors.text.primary }}>
                        Supported CSV Columns:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                        {TEMPLATE_COLS.map(c => {
                          const isRequired = c === 'Name' || c === 'Phone';
                          return (
                            <Chip
                              key={c}
                              label={c + (isRequired ? ' *' : '')}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '10.5px',
                                fontWeight: 600,
                                bgcolor: isRequired ? '#FEE4E2' : '#F2F4F7',
                                color: isRequired ? '#B42318' : '#344054',
                                border: 'none'
                              }}
                            />
                          );
                        })}
                      </Box>
                      
                      <Typography sx={{ fontSize: '11.5px', fontWeight: 700, color: themeColors.text.primary, mb: 0.5 }}>
                        Valid Industry Options:
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: themeColors.text.secondary, lineHeight: 1.5, mb: 1.5 }}>
                        {INDUSTRY_TYPES.join(', ')}
                      </Typography>

                      <Typography sx={{ fontSize: '11.5px', fontWeight: 700, color: themeColors.text.primary, mb: 0.5 }}>
                        Valid Source Options:
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: themeColors.text.secondary, lineHeight: 1.5 }}>
                        Facebook, Instagram, WhatsApp, Website, Referral, Walk-in, Phone Call, Other
                      </Typography>
                    </Paper>
                  </Collapse>
                </Box>

              </Box>
            )}

            {/* ══ STEP 1 : PREVIEW ═════════════════════════════════════════ */}
            {step === 1 && selectedFile && (
              <Box>
                {/* Summary card */}
                <Paper variant="outlined" sx={{ p: 2, mb: 2.5, borderRadius: '10px', bgcolor: '#FAFBFF', borderColor: '#E4E7EC' }}>
                  <Typography sx={{ fontSize: '12px', fontWeight: 700, color: themeColors.text.primary, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Import Summary
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>File</Typography>
                      <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: themeColors.text.primary }}>{selectedFile.name}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Total rows</Typography>
                      <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: themeColors.text.primary }}>{rowCount} records</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Default owner</Typography>
                      <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: themeColors.text.primary }}>{authStore.user?.name || 'Current user'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Destination</Typography>
                      <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: themeColors.text.primary }}>
                        {(needsOrgAndLob || needsLobOnly) ? 'Selected organisation / LOB' : 'Your assigned LOB'}
                      </Typography>
                    </Box>
                    {validationIssues.length > 0 && (
                      <Box sx={{ gridColumn: '1 / -1' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Rows to import</Typography>
                        <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: hasBlockingErrors ? '#B42318' : themeColors.text.primary }}>
                          {validRowCount} of {rowCount}
                          {hasBlockingErrors && ` (${validationIssues.filter(i => i.severity === 'error').length} rows will be skipped due to errors)`}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>

                {/* Validation issues panel */}
                <ValidationIssuesPanel issues={validationIssues} />

                {/* Preview table */}
                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: themeColors.text.secondary, mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Data Preview (first {Math.min(5, previewRows.length)} rows)
                </Typography>

                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px', maxHeight: 220, mb: 2.5 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {TEMPLATE_COLS.map(col => (
                          <TableCell key={col} sx={{ fontWeight: 700, fontSize: '11px', bgcolor: '#F8F9FA', whiteSpace: 'nowrap', py: 1 }}>
                            {col}
                            {(col === 'Name' || col === 'Phone') && (
                              <Typography component="span" sx={{ color: '#F04438', fontSize: '11px', ml: 0.25 }}>*</Typography>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewRows.slice(0, 5).map((row, i) => {
                        const rowNum = i + 2
                        const rowHasError = validationIssues.some(v => v.row === rowNum && v.severity === 'error')
                        const rowHasWarning = validationIssues.some(v => v.row === rowNum && v.severity === 'warning')
                        return (
                          <TableRow
                            key={i}
                            sx={{
                              '&:last-child td': { border: 0 },
                              bgcolor: rowHasError ? '#FEF3F2' : rowHasWarning ? '#FFFAEB' : 'transparent',
                            }}
                          >
                            {TEMPLATE_COLS.map(col => (
                              <TableCell key={col} sx={{ fontSize: '11.5px', py: 0.75, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {row[col] || <span style={{ color: '#D0D5DD', fontStyle: 'italic' }}>—</span>}
                              </TableCell>
                            ))}
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Info chips */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip size="small" label="Duplicates will be skipped & logged" sx={{ fontSize: '11px', height: 22, bgcolor: '#FEF3F2', color: '#B42318', border: '1px solid #FECDCA' }} />
                  <Chip size="small" label="Owner → current user" sx={{ fontSize: '11px', height: 22, bgcolor: '#F0F9FF', color: '#026AA2', border: '1px solid #B9E6FE' }} />
                  {hasBlockingErrors && (
                    <Chip size="small" label={`${validationIssues.filter(i => i.severity === 'error').length} rows will be skipped`} sx={{ fontSize: '11px', height: 22, bgcolor: '#FEF3F2', color: '#B42318', border: '1px solid #FECDCA' }} />
                  )}
                </Box>

                {/* Upload progress bar */}
                {isUploading && (
                  <Box sx={{ mt: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {uploadPct < 100 ? 'Uploading file…' : 'Finalizing queue…'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {uploadPct}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={uploadPct}
                      sx={{
                        height: 6, borderRadius: 3,
                        bgcolor: '#E4E7EC',
                        '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: themeColors.primary },
                      }}
                    />
                  </Box>
                )}

                {/* Inline upload error panel */}
                {uploadError && (
                  <UploadErrorPanel error={uploadError} onDismiss={() => setUploadError(null)} />
                )}
              </Box>
            )}

            {/* ══ STEP 2 : PROCESSING ══════════════════════════════════════ */}
            {step === 2 && jobId && (
              <Box sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                  <CircularProgress size={52} thickness={4} sx={{ color: themeColors.primary, mb: 2 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: '15px', color: themeColors.text.primary }}>
                    {jobStatus?.status === 'Queued' ? 'Waiting in Queue...' : jobStatus?.status === 'CancelRequested' ? 'Cancelling Import...' : 'Importing Leads...'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Processed {jobStatus?.processedRows || 0} of {jobStatus?.totalRows || rowCount} records
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontWeight: 600 }}>
                    Duration: {formatDuration(elapsedSeconds)}
                  </Typography>
                </Box>

                {/* Progress bar */}
                <Box sx={{ mb: 4 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, Math.round((((jobStatus?.processedRows || 0) / (jobStatus?.totalRows || rowCount)) * 100)))}
                    sx={{
                      height: 8, borderRadius: 4,
                      bgcolor: '#E4E7EC',
                      '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: themeColors.primary },
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
                    <Typography variant="caption" color="text.secondary">
                      {Math.min(100, Math.round((((jobStatus?.processedRows || 0) / (jobStatus?.totalRows || rowCount)) * 100)))}% complete
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Batch processing running on Hangfire
                    </Typography>
                  </Box>
                </Box>

                {/* Live counts */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
                  {[
                    { label: 'Imported', value: jobStatus?.succeededCount || 0, color: '#027A48', bg: '#F6FEF9', border: '#12B76A' },
                    { label: 'Skipped', value: jobStatus?.skippedCount || 0, color: '#B54708', bg: '#FFFAEB', border: '#F79009' },
                    { label: 'Failed', value: jobStatus?.failedCount || 0, color: '#B42318', bg: '#FEF3F2', border: '#F04438' },
                  ].map(stat => (
                    <Paper key={stat.label} variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: stat.bg, borderColor: `${stat.border}30`, borderRadius: '10px' }}>
                      <Typography variant="caption" sx={{ color: stat.color, fontWeight: 700, display: 'block' }}>{stat.label.toUpperCase()}</Typography>
                      <Typography sx={{ fontSize: '24px', fontWeight: 800, color: stat.color, lineHeight: 1.2 }}>{stat.value}</Typography>
                    </Paper>
                  ))}
                </Box>
              </Box>
            )}

            {/* ══ STEP 3 : DONE / RESULTS ══════════════════════════════════ */}
            {step === 3 && jobStatus && (
              <Box>
                <Box sx={{ textAlign: 'center', pt: 1, pb: 2 }}>
                  {jobStatus.status === 'Completed' ? (
                    <>
                      <CheckCircleIcon sx={{ fontSize: 52, color: '#12B76A', mb: 1 }} />
                      <Typography sx={{ fontWeight: 700, fontSize: '16px', color: themeColors.text.primary }}>
                        Import Complete
                      </Typography>
                    </>
                  ) : jobStatus.status === 'Cancelled' ? (
                    <>
                      <WarningIcon sx={{ fontSize: 52, color: '#F79009', mb: 1 }} />
                      <Typography sx={{ fontWeight: 700, fontSize: '16px', color: themeColors.text.primary }}>
                        Import Cancelled
                      </Typography>
                    </>
                  ) : (
                    <>
                      <ErrorIcon sx={{ fontSize: 52, color: '#F04438', mb: 1 }} />
                      <Typography sx={{ fontWeight: 700, fontSize: '16px', color: themeColors.text.primary }}>
                        Import Failed
                      </Typography>
                      {jobStatus.errorMessage && (
                        <Alert severity="error" sx={{ mt: 1.5, borderRadius: '8px', fontSize: '12.5px', textAlign: 'left' }}>
                          {jobStatus.errorMessage}
                        </Alert>
                      )}
                    </>
                  )}
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {jobStatus.succeededCount} leads were successfully imported in {formatDuration(elapsedSeconds)}.
                  </Typography>
                </Box>

                {/* Final stats */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5, mb: 3 }}>
                  {[
                    { label: 'Succeeded', value: jobStatus.succeededCount, border: '#12B76A', bg: '#F6FEF9', color: '#027A48' },
                    { label: 'Skipped',   value: jobStatus.skippedCount,   border: '#F79009', bg: '#FFFAEB', color: '#B54708' },
                    { label: 'Failed',    value: jobStatus.failedCount,     border: '#F04438', bg: '#FEF3F2', color: '#B42318' },
                  ].map(s => (
                    <Paper key={s.label} variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderColor: `${s.border}50`, bgcolor: s.bg, borderRadius: '10px' }}>
                      <Typography variant="caption" sx={{ color: s.color, fontWeight: 700, display: 'block' }}>{s.label.toUpperCase()}</Typography>
                      <Typography sx={{ fontSize: '26px', fontWeight: 800, color: s.color, lineHeight: 1.2 }}>{s.value}</Typography>
                    </Paper>
                  ))}
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Paginated logs */}
                {logsResult && logsResult.items.length > 0 ? (
                  <Box sx={{ mt: 1 }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: themeColors.text.secondary, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                      Validation & Skip Logs
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px', maxHeight: 220, mb: 1.5 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: '11px', bgcolor: '#F8F9FA', width: 80 }}>Row</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '11px', bgcolor: '#F8F9FA', width: 90 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '11px', bgcolor: '#F8F9FA' }}>Message</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {logsResult.items.map((log) => {
                            const isSkip = log.status.toLowerCase().includes('skipped')
                            const color = isSkip ? '#B54708' : '#B42318'
                            const bg = isSkip ? '#FFFAEB' : '#FEF3F2'
                            return (
                              <TableRow key={log.id}>
                                <TableCell sx={{ fontSize: '11.5px', py: 0.75, fontWeight: 700 }}>Row {log.rowNumber}</TableCell>
                                <TableCell sx={{ fontSize: '11.5px', py: 0.75 }}>
                                  <Chip
                                    label={log.status}
                                    size="small"
                                    sx={{ height: 18, fontSize: '10px', fontWeight: 700, bgcolor: bg, color }}
                                  />
                                </TableCell>
                                <TableCell sx={{ fontSize: '11.5px', py: 0.75, color: '#344054' }}>{log.message}</TableCell>
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Pagination controls */}
                    {logsResult.totalPages > 1 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1 }}>
                        <Button
                          size="small"
                          disabled={logsPage === 1}
                          onClick={() => fetchLogs(jobId!, logsPage - 1)}
                          sx={{ textTransform: 'none', fontSize: '11px' }}
                        >
                          &lt; Previous
                        </Button>
                        <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                          Page {logsPage} of {logsResult.totalPages} (Total: {logsResult.totalCount})
                        </Typography>
                        <Button
                          size="small"
                          disabled={logsPage === logsResult.totalPages}
                          onClick={() => fetchLogs(jobId!, logsPage + 1)}
                          sx={{ textTransform: 'none', fontSize: '11px' }}
                        >
                          Next &gt;
                        </Button>
                      </Box>
                    )}
                  </Box>
                ) : (jobStatus.skippedCount > 0 || jobStatus.failedCount > 0) ? (
                  <Alert severity="warning" sx={{ borderRadius: '8px', fontSize: '12px', mt: 2 }}>
                    {jobStatus.skippedCount} item(s) skipped and/or {jobStatus.failedCount} item(s) failed. Fetching log details...
                  </Alert>
                ) : (
                  <Alert severity="success" sx={{ borderRadius: '8px', fontSize: '12px', mt: 2 }}>
                    No warnings or errors — clean import!
                  </Alert>
                )}
              </Box>
            )}
          </Box>

          {/* ── Footer Actions ── */}
          <Box sx={{ px: 2.5, py: 2, borderTop: `1px solid ${themeColors.border}`, display: 'flex', justifyContent: 'flex-end', gap: 1.5, bgcolor: '#fff', flexShrink: 0 }}>
            {step === 0 && (
              <>
                <Button
                  onClick={handleAttemptClose}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, color: themeColors.text.secondary, borderColor: themeColors.border, border: `1px solid ${themeColors.border}` }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  disabled={!canGoPreview}
                  endIcon={<NextIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setStep(1)}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, bgcolor: themeColors.primary, boxShadow: 'none', '&:hover': { bgcolor: themeColors.primaryDark, boxShadow: 'none' } }}
                >
                  Preview
                </Button>
              </>
            )}

            {step === 1 && (
              <>
                {isUploading ? (
                  <Button
                    color="error"
                    variant="outlined"
                    onClick={() => uploadController?.abort()}
                    sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
                  >
                    Cancel Upload
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={() => setStep(0)}
                      sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, color: themeColors.text.secondary, border: `1px solid ${themeColors.border}` }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleImport}
                      sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, minWidth: 160, bgcolor: themeColors.primary, boxShadow: 'none', '&:hover': { bgcolor: themeColors.primaryDark, boxShadow: 'none' } }}
                    >
                      {hasBlockingErrors
                        ? `Import ${validRowCount} Valid Rows`
                        : 'Start Import'
                      }
                    </Button>
                  </>
                )}
              </>
            )}

            {step === 2 && (
              <>
                <Typography sx={{ fontSize: '11px', color: 'text.secondary', flex: 1, alignSelf: 'center' }}>
                  Closing this drawer will NOT interrupt the background processing.
                </Typography>
                <Button
                  onClick={() => {
                    bulkUploadStore.setOpen(false)
                    if (onClose) onClose()
                  }}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, color: themeColors.text.secondary, border: `1px solid ${themeColors.border}` }}
                >
                  Hide Drawer
                </Button>
                {(!jobStatus || jobStatus.status === 'Queued' || jobStatus.status === 'Processing') && (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleCancel}
                    sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
                  >
                    Cancel Import
                  </Button>
                )}
              </>
            )}

            {step === 3 && (
              <Button
                variant="contained"
                onClick={handleAttemptClose}
                sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, bgcolor: themeColors.primary, boxShadow: 'none', '&:hover': { bgcolor: themeColors.primaryDark, boxShadow: 'none' } }}
              >
                Close
              </Button>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Confirm discard */}
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false)
          bulkUploadStore.setOpen(false)
          if (onClose) onClose()
          bulkUploadStore.reset()
        }}
        title="Discard this import?"
        description={<>You've loaded <strong>{selectedFile?.name}</strong>. Closing now will discard all progress.</>}
        confirmLabel="Yes, discard"
        cancelLabel="Keep editing"
        confirmColor="error"
      />

      {/* Minimized Background Progress Card */}
      {!open && jobId && (step === 2 || step === 3) && (
        <Paper
          onClick={() => {
            bulkUploadStore.setOpen(true, true)
            if (onOpen) onOpen()
          }}
          variant="outlined"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: 320,
            zIndex: 1300,
            borderRadius: '12px',
            boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.12)',
            border: '1px solid rgba(228, 231, 236, 0.8)',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
            p: 2.5,
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            animation: 'slideIn 0.3s ease-out',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            '&:hover': {
              boxShadow: '0px 12px 35px rgba(0, 0, 0, 0.18)',
              transform: 'translateY(-4px)',
              borderColor: `${themeColors.primary}50`,
            },
            '@keyframes slideIn': {
              '0%': { transform: 'translateY(100px)', opacity: 0 },
              '100%': { transform: 'translateY(0)', opacity: 1 },
            },
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {step === 2 ? (
                <CircularProgress size={16} thickness={5} sx={{ color: themeColors.primary }} />
              ) : jobStatus?.status === 'Completed' ? (
                <CheckCircleIcon sx={{ fontSize: 16, color: '#12B76A' }} />
              ) : jobStatus?.status === 'Cancelled' ? (
                <WarningIcon sx={{ fontSize: 16, color: '#F79009' }} />
              ) : (
                <ErrorIcon sx={{ fontSize: 16, color: '#F04438' }} />
              )}
               <Typography sx={{ fontSize: '13px', fontWeight: 700, color: themeColors.text.primary }}>
                {step === 2
                  ? jobStatus?.status === 'Queued' ? 'Queued in background...' : jobStatus?.status === 'CancelRequested' ? 'Cancelling...' : 'Importing leads...'
                  : jobStatus?.status === 'Completed' ? 'Import Complete' : jobStatus?.status === 'Cancelled' ? 'Import Cancelled' : 'Import Failed'
                }
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleDismissProgress}
              sx={{ p: 0.25, color: '#98A2B3', '&:hover': { color: '#667085' } }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>

          {/* File Name */}
          <Typography sx={{ fontSize: '11.5px', color: 'text.secondary', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
            {selectedFile?.name || jobStatus?.fileName || 'leads_import.csv'}
          </Typography>

          {/* Progress Bar (only if step 2) */}
          {step === 2 && (
            <Box sx={{ my: 0.5 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, Math.round((((jobStatus?.processedRows || 0) / (jobStatus?.totalRows || rowCount)) * 100)))}
                sx={{
                  height: 4, borderRadius: 2,
                  bgcolor: '#E4E7EC',
                  '& .MuiLinearProgress-bar': { borderRadius: 2, bgcolor: themeColors.primary },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography sx={{ fontSize: '10.5px', color: 'text.secondary' }}>
                  {Math.min(100, Math.round((((jobStatus?.processedRows || 0) / (jobStatus?.totalRows || rowCount)) * 100)))}% complete
                </Typography>
                <Typography sx={{ fontSize: '10.5px', color: 'text.secondary', fontWeight: 600 }}>
                  {jobStatus?.processedRows || 0}/{jobStatus?.totalRows || rowCount}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Stats counts */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#12B76A' }} />
              <Typography sx={{ fontSize: '11px', color: '#027A48', fontWeight: 700 }}>
                {jobStatus?.succeededCount || 0}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#F79009' }} />
              <Typography sx={{ fontSize: '11px', color: '#B54708', fontWeight: 700 }}>
                {jobStatus?.skippedCount || 0}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#F04438' }} />
              <Typography sx={{ fontSize: '11px', color: '#B42318', fontWeight: 700 }}>
                {jobStatus?.failedCount || 0}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '11px', color: 'text.secondary', ml: 'auto', fontWeight: 500 }}>
              {formatDuration(elapsedSeconds)}
            </Typography>
          </Box>

          {/* Cancel button if actively processing */}
          {step === 2 && (!jobStatus || jobStatus.status === 'Queued' || jobStatus.status === 'Processing') && (
            <Button
              size="small"
              color="error"
              variant="outlined"
              onClick={async (e) => {
                e.stopPropagation();
                await handleCancel();
              }}
              sx={{
                width: '100%',
                fontSize: '11.5px',
                py: 0.5,
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                mt: 0.5
              }}
            >
              Cancel Import
            </Button>
          )}
        </Paper>
      )}
    </>
  )
})
